import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { GenericConfirmationDialogComponent } from '../../../../shared/components/generic-confirmation-dialog/generic-confirmation-dialog.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { BeUtil, FeUtil, TYPE } from '../../../../hezky-routes';
import { finalize, take } from 'rxjs';
import { ChecklistViewComponent } from '../../../../shared/components/modals/checklist-view/checklist-view.component';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-trip-timeline',
  templateUrl: './trip-timeline.component.html',
})
export class TripTimelineComponent implements OnChanges {
  @Input({ required: true }) trip!: any;
  @Input({ required: true }) lastLeg!: any;
  @Input({ required: true }) sequencedLegs!: any[];
  @Input({ required: false }) encodedPolyline: string | null = null;

  @Output() refresh = new EventEmitter<'REFRESH'>();
  @Output() generateReport = new EventEmitter<boolean>();

  protected remarks: string | null = null;
  protected modal: { [key: string]: boolean } = {
    fileUploadModal: false,
    fileViewModal: false,
    isSensorWarning: false,
  }
  protected loaders: { [key: string]: boolean } = {
    endLeg: false,
    uploadChecklist: false,
    inventoryMovement: false,
  }
  protected loadingChecklistFiles: NzUploadFile[] = [];
  protected unloadingChecklistFiles: NzUploadFile[] = [];
  protected checklist: any = null;
  protected isEndLegInProgress: boolean = false;
  protected selectedLeg: any = null;
  protected firstActiveLeg:any = null;


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['trip']) {
      this.firstActiveLeg = this.sequencedLegs.find((leg: any) => leg.leg_id === this.trip?.active_leg_id);
    }
  }

  protected endLeg({ leg_id, checklist }: { leg_id: number, checklist: any }): void {
    this.isEndLegInProgress = true;
    this.selectedLeg = { leg_id, remarks: null };
    this.feUtil.modalSvc.create<GenericConfirmationDialogComponent, TYPE.GenericConfirmationDialog>({
      nzTitle: 'Please add remarks?',
      nzContent: GenericConfirmationDialogComponent,
      nzData: { displayInput: ['remarks'] },
      nzCentered: true,
      nzMaskClosable: false,
      nzClosable: false,
      nzCloseOnNavigation: true,
    }).afterClose.pipe(untilDestroyed(this)).subscribe({
      next: (res: any) => {
        if (res?.remarks) {
          this.selectedLeg.remarks = res?.remarks;
          if (checklist && Object.keys(checklist).length === 0) {
            this.feUtil.message.info('No checklists added. Please add checklists before ending the leg.', { nzDuration: 2000 });
            this.modal['fileUploadModal'] = true;
            return;
          }
          if (this.isLastLeg(leg_id)) {
            this.modal['isSensorWarning'] = true;
          } else {
            this.legEndHandler();
          }
        }
      }
    });
  }

  private isLastLeg(leg_id: number): boolean {
    let lastSequence = this.trip.sequence[this.trip.sequence.length - 1];
    return leg_id === lastSequence;
  }

  protected endTrip(): void {
    this.modal['isSensorWarning'] = false;
    this.legEndHandler('SENSOR_WARNING');
  }

  protected beforeUploadLoadingPdf = (file: NzUploadFile, _fileList: NzUploadFile[]): boolean => {
    this.loadingChecklistFiles = [...this.loadingChecklistFiles, file];
    return false;
  };

  protected beforeUploadUnloadingPdf = (file: NzUploadFile, _fileList: NzUploadFile[]): boolean => {
    this.unloadingChecklistFiles = [...this.unloadingChecklistFiles, file];
    return false;
  };

  protected saveChecklists(): void {
    const { user_id } = this.feUtil.user_data;
    const leg_id = this.selectedLeg?.leg_id || this.firstActiveLeg.leg_id;

    let loadingFiles = this.loadingChecklistFiles.map(file => {
      let actualFile = file.originFileObj as File;
      if (!actualFile && file instanceof File) {
        actualFile = file as any;
      }
      if (actualFile && typeof (actualFile as any).uid !== 'undefined') {
        delete (actualFile as any).uid;
      }
      return actualFile || file;
    });
    let unloadingFiles = this.unloadingChecklistFiles.map(file => {
      let actualFile = file.originFileObj as File;
      if (!actualFile && file instanceof File) {
        actualFile = file as any;
      }
      if (actualFile && typeof (actualFile as any).uid !== 'undefined') {
        delete (actualFile as any).uid;
      }
      return actualFile || file;
    });

    if (loadingFiles.length === 0 && unloadingFiles.length === 0) {
      this.feUtil.message.error('Please upload at least one checklist.', { nzDuration: 2000 });
      return;
    }

    const appendFiles = (form: FormData, key: string, files: (File | any)[]) => {
      files.forEach(f => {
        const file = f instanceof File ? f : f.originFileObj;
        if (file) {
          form.append(key, file, file.name);
        }
      });
    };

    const formData = new FormData();
    formData.append('user_id', String(user_id));
    formData.append('leg_id', String(leg_id));
    appendFiles(formData, 'loading', loadingFiles);
    appendFiles(formData, 'unloading', unloadingFiles);
    this.loaders['uploadChecklist'] = true;
    this.beUtil.tripsSvc.uploadChecklist(this.trip.trip_id, formData)
    .pipe(take(1), untilDestroyed(this), finalize(() => this.loaders['uploadChecklist'] = false))
    .subscribe({
      next: (res: any) => {
        if (res?.status !== 200) return;
        this.loadingChecklistFiles = [];
        this.unloadingChecklistFiles = [];
        this.feUtil.message.success('Checklists file upload successful!', { nzDuration: 2000 });
        if (!this.isEndLegInProgress && this.selectedLeg?.status !== 'completed') {
          this.patchInventoryMovement('paused');
          this.modal['fileUploadModal'] = false;
          return;
        }
        if (this.selectedLeg?.status !== 'completed' && this.isEndLegInProgress) {
          this.legEndHandler('NORMAL');
          this.modal['fileUploadModal'] = false;
          return;
        }
        if (this.selectedLeg?.status === 'completed') {
          this.modal['fileUploadModal'] = false;
          this.refresh.emit('REFRESH');
          return;
        }
      }
    });
  }

  protected skipChecklists(): void {
    if (this.selectedLeg?.status === 'completed') {
      this.modal['fileUploadModal'] = false;
    } else if (this.isEndLegInProgress) {
      this.legEndHandler();
      this.modal['fileUploadModal'] = false;
    } else {
      this.patchInventoryMovement('paused');
      this.modal['fileUploadModal'] = false;
    }
    this.loadingChecklistFiles = [];
    this.unloadingChecklistFiles = [];
  }

  protected inventoryMovementHandler(status: 'paused' | 'active'): void {
    if (status === 'paused') {
      if(this.firstActiveLeg?.checklist && Object.keys(this.firstActiveLeg.checklist).length == 0) {
        this.openUploadChecklistsModal(null);
        return;
      } else {
        this.patchInventoryMovement(status);
        return;
      }
    }
    else {
      this.patchInventoryMovement(status);
      this.modal['fileUploadModal'] = false;
      return;
    }
  }

  private patchInventoryMovement(status: 'paused' | 'active') {
    this.loaders['inventoryMovement'] = true;
    let payload = {
      status: status,
    }
    this.beUtil.tripsSvc.patchTrip(this.trip.trip_id, payload)
      .pipe(take(1), untilDestroyed(this), finalize(() => this.loaders['inventoryMovement'] = false))
      .subscribe({
        next: (res: any) => {
          if (res?.status !== 200) return;
          this.refresh.emit('REFRESH');
          this.feUtil.message.success(`Inventory movement has been ${status === 'paused' ? 'started' : 'ended'}.`, { nzDuration: 2000 });
        },
      });
  }

  private legEndHandler(type: 'SENSOR_WARNING' | 'NORMAL' = 'NORMAL'): void {
    if (type !== 'SENSOR_WARNING') {
      if (this.isLastLeg(this.selectedLeg?.leg_id)) {
        this.modal['isSensorWarning'] = true;
        return;
      }
    }
    let payload: any = {
      legs: [
        {
          leg_id: this.selectedLeg?.leg_id,
          markers: this.sequencedLegs.map((leg: any) => ({
            lat: leg.location.latitude,
            lng: leg.location.longitude,
          })),
          status: 'completed',
        }
      ],
      remark: this.selectedLeg?.remarks || null
    }
    if (this.encodedPolyline) {
      payload.legs[0]['encodedPolyline'] = this.encodedPolyline;
    }
    this.loaders['endLeg'] = true;
    this.beUtil.tripsSvc.patchTrip(this.trip.trip_id, payload)
      .pipe(take(1), untilDestroyed(this), finalize(() => this.loaders['endLeg'] = false))
      .subscribe({
        next: (res: any) => {
          if (res?.status !== 200) {
            this.feUtil.message.error(res?.message || 'Failed to end leg');
            return;
          }
          this.refresh.emit('REFRESH');
          this.isEndLegInProgress = false;
          this.selectedLeg = null;
          this.remarks = null;
        }
      });
  }

  protected openUploadChecklistsModal(leg: any) {
    this.selectedLeg = leg;
    this.modal['fileUploadModal'] = true;
  }

  protected viewChecklist(checklist: any): void {
    this.checklist = checklist;
    this.modal['fileViewModal'] = true;
  }
  constructor(protected feUtil: FeUtil, public beUtil: BeUtil) { }
}
